import type { SchemaBooleanExpressionType } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import type { PartialFlowStep } from "@redotech/redo-flows-common/flow-in-progress";
import {
  recognizeConditionFields,
  type RecognizeConditionField,
} from "@redotech/redo-flows-common/schemas/recognize";
import { ConditionDataSource } from "@redotech/redo-flows-common/steps";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import type { NumberComparisonExpression } from "@redotech/redo-flows-core-schema-common/expressions/number-expression";
import { useTranslation } from "@redotech/redo-i18n";
import {
  recognizeFollowerCountComparisons,
  recognizeFollowerCountPlatforms,
} from "@redotech/redo-recognize-actions-common/actions-flow";
import {
  REDO_DEFAULT_CRITERIA,
  RedoCriterionKey,
} from "@redotech/redo-recognize-common/criteria";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { DeepPartial } from "@redotech/util/type";
import { memo, type ReactNode } from "react";
import type { StepTypeDetailsProps } from ".";

interface RecognizeConditionInputsProps extends Pick<
  StepTypeDetailsProps<PartialFlowStep>,
  "setStep"
> {
  step: Extract<PartialFlowStep, { type: StepType.CONDITION }>;
  expression?: DeepPartial<SchemaBooleanExpressionType>;
  fieldPicker: ReactNode;
}

export const RecognizeConditionInputs = memo(function RecognizeConditionInputs({
  expression,
  fieldPicker,
  setStep,
  step,
}: RecognizeConditionInputsProps) {
  const { t } = useTranslation();
  const isFollowerCountCondition = expression?.type === "number_comparison";

  if (isFollowerCountCondition) {
    const followerCountExpression = expression;
    const platformOptions = recognizeFollowerCountPlatforms.map(
      ({ field, label }) => ({
        id: field,
        type: "text" as const,
        text: label,
        value: field,
      }),
    );
    const comparisonOptions = recognizeFollowerCountComparisons.map(
      ({ operator, label }) => ({
        id: operator,
        type: "text" as const,
        text: label,
        value: operator,
      }),
    );

    function updateFollowerCountCondition(
      changes: Partial<NumberComparisonExpression>,
    ) {
      setStep({
        ...step,
        expression: {
          dataSource: ConditionDataSource.TriggerData,
          schemaBooleanExpression: {
            ...followerCountExpression,
            ...changes,
            type: "number_comparison",
          },
        },
      });
    }

    return (
      <Flex dir="column" gap="md" w="full">
        {fieldPicker}
        <RedoSingleSelectDropdownInput
          label={t("Social media")}
          options={platformOptions}
          optionSelected={({ item }) =>
            updateFollowerCountCondition({ field: item.value })
          }
          placeholder={t("Select a social platform")}
          selectedItem={platformOptions.find(
            ({ value }) => value === expression.field,
          )}
          size="xs"
        />
        <RedoSingleSelectDropdownInput
          label={t("Comparison")}
          options={comparisonOptions}
          optionSelected={({ item }) =>
            updateFollowerCountCondition({ operator: item.value })
          }
          selectedItem={comparisonOptions.find(
            ({ value }) => value === expression.operator,
          )}
          size="xs"
        />
        <RedoTextInput
          inputTypeHint="number"
          label={t("Follower count")}
          setValue={(value) => {
            let comparisonValue: number | undefined;
            if (value !== "") {
              comparisonValue = Number(value);
            }
            updateFollowerCountCondition({ comparisonValue });
          }}
          size="sm"
          value={expression.comparisonValue?.toString() ?? ""}
        />
      </Flex>
    );
  }

  const criterionLabels: Record<RecognizeConditionField, string> = {
    isVip: t("VIP"),
    homeValue: REDO_DEFAULT_CRITERIA[RedoCriterionKey.HomeValue].displayName,
    maxPlatformFollowers:
      REDO_DEFAULT_CRITERIA[RedoCriterionKey.MaxPlatformFollowers].displayName,
    investorOrWealthManager:
      REDO_DEFAULT_CRITERIA[RedoCriterionKey.InvestorOrWealthManager]
        .displayName,
  };
  const criterionOptions: TextRedoListItem<RecognizeConditionField>[] =
    recognizeConditionFields.map((field) => ({
      id: field,
      type: "text",
      text: criterionLabels[field],
      value: field,
    }));
  const selectedCriterion = criterionOptions.find(
    (option) => option.value === expression?.field,
  );

  return (
    <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
      {fieldPicker}
      <Text fontSize="xs" fontWeight="medium">
        {t("is")}
      </Text>
      <RedoSingleSelectDropdownInput
        fitToAnchor={false}
        flexGrow={false}
        options={criterionOptions}
        optionSelected={({ item }) => {
          setStep({
            ...step,
            expression: {
              dataSource: ConditionDataSource.TriggerData,
              schemaBooleanExpression: {
                type: "boolean_evaluation",
                field: item.value,
                value: true,
              },
            },
          });
        }}
        placeholder={t("Select a criterion")}
        selectedItem={selectedCriterion}
        size="xs"
      />
    </Flex>
  );
});
